import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from '../../../../i18n';

import { Checkbox } from '../../../element';

import { useProvider } from '../../../../entities/providers/hooks';
import * as fromSelectors from '../../../../entities/providers/selectors';
import { setDocs } from '../../../../entities/providers';

import { RuleContent } from './rules.styled';

import { isDocsRule } from '../../../../config/providers';
import { priceCurrencyToSign } from '../../../../helpers/currency';

const Rule: React.FC<{ checked: boolean; rule: string; onChange?: () => void }> = ({ rule, checked, onChange }) => (
  <div className="mb-20">
    <Checkbox checked={checked} onChange={onChange} />
    {/* eslint-disable-next-line react/no-danger */}
    <RuleContent dangerouslySetInnerHTML={{ __html: rule }} />
  </div>
);

export const RatesRules: React.FC<{ onChange(allChecked: boolean): void }> = ({ onChange }) => {
  const [t] = useTranslation('rates');
  const dispatch = useDispatch();

  const toCountry = useSelector(fromSelectors.getInboundCountry);
  const service = useSelector(fromSelectors.getBookService);
  const noDocs = useSelector(fromSelectors.isWithoutDocs);
  const discount = useSelector(fromSelectors.getNoDocsDiscountPrice);
  const discountFormatted = discount ? priceCurrencyToSign(discount) : null;

  const providerConfig = useProvider('sender');

  const rules = useMemo(() => {
    const list = Array.isArray(providerConfig.rules[service]) ? [...providerConfig.rules[service]] : [];

    // hide 4th rule
    if (!discount && list.length > 3) {
      list.splice(3, 1);
    }

    return list.map((rule) => rule.replace('[provider]', toCountry.provider));
  }, [service, providerConfig.rules, discount, toCountry.provider]);

  const [checkboxes, setState] = useState(() => rules.map(() => false));

  const toggleDocs = useCallback(() => {
    dispatch(setDocs({ noDocs: !noDocs }));
  }, [noDocs]);

  const handleChange = useCallback(
    (idx: number) => () => {
      const state = [...checkboxes];
      state[idx] = !state[idx];

      // handle no docs
      if (isDocsRule(rules[idx])) {
        toggleDocs();
      }

      setState(state);

      // TODO: for now use only first 3 checkboxes
      onChange(state.slice(0, 3).every(Boolean));
    },
    [rules, checkboxes]
  );

  return (
    <>
      {rules.map((rule, idx) => (
        <Rule
          key={rule}
          rule={t(rule, { discount: discountFormatted })}
          checked={checkboxes[idx]}
          onChange={handleChange(idx)}
        />
      ))}
    </>
  );
};
